import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { MARKET_NAMES } from '@hc/shared';

// Shared presentational primitives for the platform's filter modals,
// extracted so the Screener and Operating Pressure filters render with
// one identical visual language (Saxo-style: centered dialog, collapsible
// rows with a value-pill summary, vertical checkbox multi-selects).
//
// The styling here mirrors screener-filter-modal-v2.tsx exactly. That
// modal predates this kit and still carries its own copies; it should be
// migrated onto these primitives in a follow-up so there's a single
// source of truth. For now the values are kept in lockstep by hand.

/** Option for a multi-select list: a stable `value` (the key stored in
 *  the selection Set) plus a human `label` for display. */
export interface SelectOption {
  value: string;
  label: string;
  /** Indent level for a hierarchical list — 0 = parent, 1 = child. Used by the
   *  sub-segment filter, where the canonical-17 parents and their fine specialty
   *  children share ONE dropdown and the nesting is the only thing that shows
   *  which is which. Omitted = 0. */
  depth?: number;
}

/** Pill that shows a filter row's current value when collapsed. Accent
 *  tint when an active selection exists; quiet tertiary text for "All". */
export function ValuePill({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--t-body)',
        padding: '2px var(--s-2)',
        background: active ? 'var(--color-accent-100)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-tertiary)',
        maxWidth: 220,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

/** Build the Saxo-style summary string: "All" | "Acme" | "Acme +3". */
export function summarizeSelection(
  selected: ReadonlySet<string>,
  labelFor: (v: string) => string,
): string {
  if (selected.size === 0) return 'All';
  const first = labelFor(Array.from(selected)[0]!);
  return selected.size === 1 ? first : `${first} +${selected.size - 1}`;
}

/** Collapsible filter row: a single top hairline, a header button with
 *  label + value pill + chevron, and arbitrary expanded content. Content-
 *  agnostic so it wraps both checkbox multi-selects and bespoke controls. */
export function CollapsibleFilterRow({
  label,
  summary,
  active,
  defaultOpen,
  children,
}: {
  label: string;
  summary: string;
  active: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? active);
  return (
    <div style={{ borderTop: '1px solid var(--hairline)', background: 'var(--surface)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <ValuePill active={active}>{summary}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && <div style={{ padding: '4px var(--s-2) var(--s-2)' }}>{children}</div>}
    </div>
  );
}

/** Vertical multi-select: "All" toggle, optional inline search (auto-
 *  shows above 8 options), scrollable checkbox list. `selected` holds
 *  option values; empty set == "All". */
export function MultiSelectList({
  options,
  selected,
  onChange,
  maxHeight = 220,
}: {
  options: readonly SelectOption[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  maxHeight?: number;
}) {
  const [query, setQuery] = useState('');
  const showSearch = options.length > 8;
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);
  const allActive = selected.size === 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      {showSearch && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-1)',
            padding: '2px var(--s-2)',
            background: 'var(--surface)',
          }}
        >
          <Search size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            placeholder="Search"
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--t-body)',
              padding: '2px 0',
              color: 'var(--text-primary)',
            }}
          />
          {query && (
            <X
              size={10}
              onClick={() => setQuery('')}
              style={{ cursor: 'pointer', color: 'var(--text-tertiary)' }}
            />
          )}
        </div>
      )}
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
        }}
      >
        <CheckboxRow
          checked={allActive}
          label="All"
          onToggle={() => {
            if (allActive) return;
            onChange(new Set());
          }}
        />
        {filtered.map((opt) => {
          const checked = selected.has(opt.value);
          return (
            <CheckboxRow
              key={opt.value}
              checked={checked}
              label={opt.label}
              depth={opt.depth}
              onToggle={() => {
                const next = new Set(selected);
                if (checked) next.delete(opt.value);
                else next.add(opt.value);
                onChange(next);
              }}
            />
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{
              padding: 'var(--s-2) var(--s-3)',
              fontSize: 'var(--t-body)',
              color: 'var(--text-tertiary)',
            }}
          >
            No matches
          </div>
        )}
      </div>
    </div>
  );
}

export function CheckboxRow({
  checked,
  label,
  onToggle,
  depth = 0,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
  /** Hierarchical indent — see SelectOption.depth. */
  depth?: number;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        padding: '4px var(--s-2)',
        paddingLeft: depth > 0 ? `calc(var(--s-2) + ${depth} * var(--s-3))` : undefined,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'var(--t-body)',
        color: 'var(--text-primary)',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          flexShrink: 0,
          borderRadius: 2,
          border: `1px solid ${checked ? 'var(--accent)' : 'var(--hairline)'}`,
          background: checked ? 'var(--accent)' : 'var(--surface)',
          color: 'var(--bg)',
        }}
      >
        {checked && <Check size={10} strokeWidth={3} />}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
}

/** Region multi-select, grouped under country headers (like the Screener's
 *  region filter). Drop INSIDE a CollapsibleFilterRow. `countries` is the set
 *  of country codes to show groups for (e.g. the panel's effective countries);
 *  selection is by region name across all of them. */
export function GroupedRegionList({
  regionsByCountry,
  countries,
  selected,
  onChange,
}: {
  regionsByCountry: Record<string, readonly string[]>;
  countries: readonly string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const visible = countries.filter((c) => (regionsByCountry[c] ?? []).length > 0);
  if (visible.length === 0) {
    return (
      <span style={{ fontSize: 'var(--t-body)', color: 'var(--text-tertiary)' }}>no values</span>
    );
  }
  const toggle = (region: string) => {
    const next = new Set(selected);
    if (next.has(region)) next.delete(region);
    else next.add(region);
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      {visible.map((c) => (
        <div key={c}>
          <div
            style={{
              padding: '4px var(--s-2) 2px',
              fontSize: 'var(--t-meta)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
            }}
          >
            {MARKET_NAMES[c as keyof typeof MARKET_NAMES] ?? c}
          </div>
          {(regionsByCountry[c] ?? []).map((region) => (
            <CheckboxRow
              key={`${c}:${region}`}
              checked={selected.has(region)}
              label={region}
              onToggle={() => toggle(region)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Shared dialog shell: centered, no shadow, radius-4, centered grid
 *  header with an X-close, scrollable body, and a Reset / Done footer.
 *  Matches the Screener filter modal exactly. */
export function FilterModalShell({
  open,
  onOpenChange,
  title,
  children,
  resetLabel,
  doneLabel,
  onReset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  resetLabel: string;
  doneLabel: string;
  onReset: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9499 }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(560px, 92vw)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 4,
            zIndex: 9500,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 36px',
              alignItems: 'center',
              padding: 'var(--s-3)',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            <span />
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--t-section)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 2,
                  color: 'var(--text-secondary)',
                  justifySelf: 'end',
                }}
              >
                <X size={14} />
              </button>
            </Dialog.Close>
          </div>

          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 'var(--s-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-2)',
            }}
          >
            {children}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              padding: 'var(--s-3)',
              borderTop: '1px solid var(--hairline)',
              background: 'var(--surface)',
            }}
          >
            <button
              type="button"
              onClick={onReset}
              style={{
                fontSize: 'var(--t-body)',
                padding: '4px 12px',
                background: 'transparent',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              {resetLabel}
            </button>
            <div style={{ flex: 1 }} />
            <Dialog.Close asChild>
              <button
                type="button"
                data-accent="true"
                style={{
                  fontSize: 'var(--t-body)',
                  padding: '4px 16px',
                  background: 'var(--accent)',
                  border: '1px solid var(--accent)',
                  borderRadius: 2,
                  cursor: 'pointer',
                  color: 'var(--bg)',
                  fontWeight: 600,
                }}
              >
                {doneLabel}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Confirm dialog on the same recipe as FilterModalShell — so destructive
 *  confirmations look like the rest of the product instead of a native
 *  `window.confirm`. Cancel is the default focus; the confirm button turns
 *  claret when `destructive`. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
}) {
  const confirmBg = destructive ? 'var(--color-negative-500)' : 'var(--accent)';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9499 }}
        />
        <Dialog.Content
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(420px, 92vw)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 4,
            zIndex: 9500,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 36px',
              alignItems: 'center',
              padding: 'var(--s-3)',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            <span />
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--t-section)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 2,
                  color: 'var(--text-secondary)',
                  justifySelf: 'end',
                }}
              >
                <X size={14} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description
            style={{
              margin: 0,
              padding: 'var(--s-3)',
              fontSize: 'var(--t-body)',
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
            }}
          >
            {body}
          </Dialog.Description>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 'var(--s-2)',
              padding: 'var(--s-3)',
              borderTop: '1px solid var(--hairline)',
            }}
          >
            <Dialog.Close asChild>
              <button
                type="button"
                style={{
                  fontSize: 'var(--t-body)',
                  padding: '4px 12px',
                  background: 'transparent',
                  border: '1px solid var(--hairline)',
                  borderRadius: 2,
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                }}
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              data-accent="true"
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
              style={{
                fontSize: 'var(--t-body)',
                padding: '4px 16px',
                background: confirmBg,
                border: `1px solid ${confirmBg}`,
                borderRadius: 2,
                cursor: 'pointer',
                color: 'var(--bg)',
                fontWeight: 600,
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
